/* Face geometry: artwork units (0 to 1024) -> canvas CSS and device pixels -> WebGL clip
   space. Just math. The caller passes `dpr` in already capped
   (`Math.min(devicePixelRatio || 1, 2)`); this file never reads `devicePixelRatio` itself. */

/** The smallest dot radius, in CSS pixels, so the tiniest dots stay visible at a low DPR. */
export const MIN_DOT_RADIUS_CSS = 0.2;

/** Where waves start, as a fraction of the portrait box (the artwork fills it). The face
    shaders and the page's waves both use it, so the rings on the face and the text line up. */
export const WAVE_ORIGIN = { x: 0.175, y: 0.5 } as const;

export interface FaceLayout {
  /** The scale from artwork units (0 to 1024) to CSS pixels in the box. */
  fScale: number;
  /** The canvas width in CSS pixels (same as the height; the box is square). The canvas is
      exactly the box: the page clips at the box's edge, so any padding would just waste
      pixels. */
  cssW: number;
  cssH: number;
  /** The canvas's backing-store size, in device pixels (`round(cssW*dpr)`). */
  deviceW: number;
  deviceH: number;
  dpr: number;
}

/** The face canvas's layout for a square portrait box. `boxCss` is the box's width in CSS
    pixels (same as the height). */
export function computeFaceLayout(boxCss: number, dpr: number): FaceLayout {
  const fScale = boxCss / 1024;
  const cssW = boxCss;
  const cssH = cssW;
  const deviceW = Math.round(cssW * dpr);
  const deviceH = Math.round(cssH * dpr);
  return { fScale, cssW, cssH, deviceW, deviceH, dpr };
}

export interface ClipTransform {
  sx: number;
  sy: number;
  ox: number;
  oy: number;
  /** The smallest dot radius, `MIN_DOT_RADIUS_CSS` in clip units. It depends on `cssW`, so it's
      recomputed on resize, unlike a per-instance attribute, which is fixed at upload. */
  minRadius: number;
}

/** Maps a dot's center in artwork space, `(ax, ay)` in `[0,1024]`, to WebGL clip space
    `[-1,1]`, with Y flipped (clip space +Y is up, canvas +Y is down):
    `clipX = ax*sx + ox`, `clipY = ay*sy + oy`.

    Where that comes from: a dot's canvas position in CSS pixels is `(ax*fScale, ay*fScale)`.
    `clipX = (cssX/cssW)*2 - 1`, `clipY = 1 - (cssY/cssH)*2`. Substituting:
      sx =  2*fScale/cssW,  ox = -1
      sy = -2*fScale/cssH,  oy = 1

    The dot radius in clip units is `rArtwork * |sx|` (the shader works it out from `sx`; the
    box is square, so `|sx| === |sy|`). `minRadius` converts `MIN_DOT_RADIUS_CSS` (a floor in
    CSS pixels) to the same clip units with the width's `[0,cssW] -> [-1,1]` scale, `2/cssW`. */
export function dotClipTransform(l: FaceLayout): ClipTransform {
  const sx = (2 * l.fScale) / l.cssW;
  const ox = -1;
  const sy = (-2 * l.fScale) / l.cssH;
  const oy = 1;
  const minRadius = (2 * MIN_DOT_RADIUS_CSS) / l.cssW;
  return { sx, sy, ox, oy, minRadius };
}
